import React, { Fragment } from 'react';
import {
  DocumentDownloadLinkListWithPagination,
  RelatedContentCardListWithPagination,
} from 'components/blocks/ListWithPagination';
import ShareWidget from 'components/blocks/ShareWidget';
import { PageMeta } from 'types/contentStack';
import { nonFatalBuildError, warningWithDetail } from '../errorReporting';

export type CsBlock = {
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

type BlockProps<T extends string> = {
  [K in T]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

export type MappingEntry<T extends string = string> = [
  T,
  React.ComponentType<BlockProps<T>>,
];

export const m = <T extends string>(
  key: T,
  value: React.ComponentType<BlockProps<T>>
): MappingEntry<T> => [key, value];

const mapBlock = (
  mapping: MappingEntry[],
  block: CsBlock,
  additionalParams?: {
    pageMeta?: PageMeta;
    pageUrl?: string;
    currentPage?: number;
  }
): JSX.Element | null => {
  const key = Object.entries(block).find(([, value]) => !!value)?.[0];

  /* istanbul ignore if */
  if (!key) {
    warningWithDetail(
      'Unrecognised empty block.',
      'Have you forgotten to extend the page query with a new block type?'
    );
    return null;
  }

  const Block = mapping.find(([k]) => k === key)?.[1];

  /* istanbul ignore if */
  if (!Block) {
    nonFatalBuildError(
      `Unrecognised block of type '${key}'.`,
      'Have you forgotten to update the block mapping?'
    );
    return null;
  }

  if (Block === (ShareWidget as unknown)) {
    return (
      <Block
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...{ [key]: block[key] }}
        canonical_url={additionalParams?.pageMeta?.canonical_tag}
        page_url={additionalParams?.pageUrl}
      />
    );
  }

  if (
    Block === (RelatedContentCardListWithPagination as unknown) ||
    Block === (DocumentDownloadLinkListWithPagination as unknown)
  ) {
    return (
      <Block
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...{ [key]: block[key] }}
        currentPage={additionalParams?.currentPage}
      />
    );
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Block {...{ [key]: block[key] }} />;
};

export const mapBlocksInternal =
  (mapping: MappingEntry[]) =>
  (
    blocks: CsBlock[],
    additionalParams?: {
      pageMeta?: PageMeta;
      pageUrl?: string;
      currentPage?: number;
    }
  ): JSX.Element[] =>
    blocks.map((block, i) => (
      // Blocks will never be reordered
      // eslint-disable-next-line react/no-array-index-key
      <Fragment key={i}>{mapBlock(mapping, block, additionalParams)}</Fragment>
    ));
